import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { PrismAsyncLight as SyntaxHighlighter } from 'react-syntax-highlighter';
import bash from 'react-syntax-highlighter/dist/esm/languages/prism/bash';
import json from 'react-syntax-highlighter/dist/esm/languages/prism/json';
import prism from 'react-syntax-highlighter/dist/esm/styles/prism/dracula';

import { apiEndpoint } from './../../config';
import { selectInfo, selectLoading } from '../../features/profile/profileSlice';

import { Button, Collapse, Typography, Spin } from 'antd';

import { CATEGORIES, STATUSES, MAX_ROWS,  /* CA_COUNTIES, CA_MSAS */ } from '../../constants';
import { Layout } from '../../components/Layout';

import sampleResponse from './response.json';
import styles from './Documentation.module.scss';

SyntaxHighlighter.registerLanguage('bash', bash);
SyntaxHighlighter.registerLanguage('json', json);

const { Panel } = Collapse;
const { Link, Title, Paragraph } = Typography;

const SEARCH_CODE = `
curl -H 'Content-Type: application/json' -d '{
  "token": "<ACCESS TOKEN>",
  "query": {
    "state": "CA",
    "counties": ["Los Angeles County", "Orange County"],
    "category": ["New ADU"],
    "file_date": ["2022-01-01", null]
  }
}' ${apiEndpoint}/api/v3/search
`.trim();

const PURCHASE_CODE = `
curl -H 'Content-Type: application/json' -d '{
  "token": "<ACCESS TOKEN>",
  "query": {
    "state": "CA",
    "counties": ["Los Angeles County", "Orange County"],
    "category": "New ADU",
    "file_date": ["2022-01-01", null]
  },
  "count": 13087
}' ${apiEndpoint}/api/v3/purchase
`.trim();

const PURCHASE_RESPONSE = {
  "purchase_id": "aabbccdd-eeff-0000-1111-222233334444",
  "url": "https://builty.s3.amazonaws.com/portal/aabbccdd-eeff-0000-1111-222233334444.csv?...",
  "free_lookups": 123,
};


export const Documentation = ({ isLoggedIn, onLogin }) => {
  const profileLoading = useSelector(selectLoading);
  const { apiTokens } = useSelector(selectInfo);

  const [tokenVisible, setTokenVisible] = useState(false);

  const token = apiTokens && apiTokens.length ? apiTokens[0] : null;

  return (
    <Layout.Content>
      <Title level={3}>Using the API</Title>
      <Paragraph>Builty API uses HTTP POST requests, with both request data and response data encoded as JSON.</Paragraph>

      <Title level={4}>Create an access token</Title>

      { !isLoggedIn
        ? <Paragraph>Please <Button type="link" size="small" onClick={ onLogin }>log in</Button> first.</Paragraph>
        : profileLoading
        ? <Spin />
        : !apiTokens || !apiTokens.length
        ? <Paragraph>Please go to the <Link href="/api-usage">API Usage</Link> page to create access tokens.</Paragraph>
        : <Paragraph>
            Congratulations, you already have an access token: {
              tokenVisible ? <code>{ token }</code> : <Button type="link" onClick={ () => { setTokenVisible(true) } }>show</Button>
            }
          </Paragraph>
      }

      <Title level={4}>Run a search query</Title>

      <Paragraph>
        A search query takes a set of parameters and returns the number of results (up to {MAX_ROWS}) and a preview of the first 10 results.
        The <code>state</code> parameter is required; all others are optional &ndash;
        however, at least some need to be set to avoid going over the maximum number of results.
      </Paragraph>

      <Paragraph>The API endpoint is <code>{ apiEndpoint }/api/v3/search</code>.</Paragraph>

      <Paragraph>It accepts the following parameters:</Paragraph>

      <Paragraph>
        <table className={ styles.objectFormat }>
          <thead>
            <tr>
              <th>Name</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><code>state</code> <em>string, <strong>required</strong></em></td>
              <td>State abbreviation.<br/>E.g.: <code>CA</code></td>
            </tr>
            <tr>
              <td><code>counties</code> <em>string[]</em></td>
              <td>List of counties.<br />
               E.g.: <code>["Los Angeles County", "Santa Barbara County"]</code>
              </td>
            </tr>
            <tr>
              <td><code>city</code> <em>string</em></td>
              <td>City</td>
            </tr>
            <tr>
              <td><code>zipcode</code> <em>string</em></td>
              <td>5-digit zipcode</td>
            </tr>
            <tr>
              <td><code>street</code> <em>string</em></td>
              <td>Street address: house number, street name, and unit number. Must be the complete value; partial matches are not supported.<br/>
                <strong>Note</strong>: "123 South Main Street" and "123 S Main St" are considered to be equal.
                "123 Main St", "123 S Main St", "123 Main St S" are considered to be different.
              </td>
            </tr>
            <tr>
              <td><code>category</code> <em>string[]</em></td>
              <td>Permit category. A permit can have 0 or more categories.<br />
                E.g.: <code>['Accessory Structure', 'Bath Remodel']</code><br/>
                <Collapse ghost>
                  <Panel header="Supported values">
                    <ul>
                      { CATEGORIES.map((value, idx) => <li key={idx}><code>{value}</code></li>) }
                    </ul>
                  </Panel>
                </Collapse>
              </td>
            </tr>

            <tr>
              <td><code>status</code> <em>string</em></td>
              <td>Permit status<br />
                <Collapse ghost>
                  <Panel header="Supported values">
                    <ul>
                      { STATUSES.map((value, idx) => <li key={idx}><code>{value}</code></li>) }
                    </ul>
                  </Panel>
                </Collapse>
              </td>
            </tr>
            <tr>
              <td><code>file_date</code> <em>[string, string]</em></td>
              <td>A range for the date the permit was filed. Start and end values must be either <code>null</code> or a string in "YYYY-MM-DD" format.<br/>
              Some jurisdictions don't provide file dates.<br /></td>
            </tr>
            <tr>
              <td><code>permit_date</code> <em>[string, string]</em></td>
              <td>A range for the date the permit was issued. Start and end values must be either <code>null</code> or a string in "YYYY-MM-DD" format.</td>
            </tr>
            <tr>
              <td><code>final_date</code> <em>[string, string]</em></td>
              <td>A range for the date the permit was finaled. Start and end values must be either <code>null</code> or a string in "YYYY-MM-DD" format.</td>
            </tr>
            <tr>
              <td><code>job_value</code> <em>[int, int]</em></td>
              <td>A range for the job value of the permit. Min and max values must be either <code>null</code> or integer dollar amounts.<br />
              Job values are not always available.
              </td>
            </tr>
            <tr>
              <td><code>contractor</code> <em>string</em></td>
              <td>Name of the contractor (or any keywords in the name)<br/>
              Contractor names are not always available.
              </td>
            </tr>
            <tr>
              <td><code>architect</code> <em>string</em></td>
              <td>Name of the architect (or any keywords in the name)<br/>
              Only a few jurisdictions provide architect names.
              </td>
            </tr>
            <tr>
              <td><code>designer</code> <em>string</em></td>
              <td>Name of the designer (or any keywords in the name)<br/>
              Only a few jurisdictions provide designer names.
              </td>
            </tr>
            <tr>
              <td><code>architect</code> <em>string</em></td>
              <td>Name of the architect (or any keywords in the name)<br/>
              Only a few jurisdictions provide architect names.
              </td>
            </tr>
          </tbody>
        </table>
      </Paragraph>

      <Paragraph>
        A successful response will have a 200 HTTP status code, and a JSON response body containing a <code>count</code> property
        with the number of permits found (up to {MAX_ROWS}) and a <code>preview</code> property with a list of 10 <Link href="#Permit">permits</Link>.
      </Paragraph>

      <Paragraph>
        An error response will have a 4xx status code, and will contain an <code>error</code> string.
      </Paragraph>

      <Title level={5}>Sample request</Title>

      <Paragraph>
        This query will search the Los Angeles MSA for new ADU permits filed after Jan 1, 2022:
      </Paragraph>

      <Paragraph>
        <SyntaxHighlighter language="bash" style={ prism }>
          { SEARCH_CODE }
        </SyntaxHighlighter>
      </Paragraph>

      <Title level={5}>Sample response</Title>

      <Paragraph>
        <SyntaxHighlighter language="json" style={ prism } className={ styles.response }>
          { JSON.stringify(sampleResponse, null, 2) }
        </SyntaxHighlighter>
      </Paragraph>

      <Title level={4}>Purchase the query results</Title>

      <Paragraph>
        The purchase API takes the same parameters as the search API, with an addition of a <code>count</code> property &ndash;
        the number of results returned by the search API. If its value does not match the actual number of permits about to be
        purchased, the API will return an error. In rare cases, it can happen if the permit data has been updated between the two
        API calls. The <code>count</code> property ensures that the cost of the purchase is predictable.
      </Paragraph>

      <Paragraph>The API endpoint is <code>{ apiEndpoint }/api/v3/purchase</code>.</Paragraph>

      <Paragraph>
        A successful response will have a 200 HTTP status code, and a JSON response body containing a unique <code>purchase_id</code>
        a <code>url</code> of the CSV file with the query results, and an updated number of remaining free lookups, <code>free_lookups</code>.
        The results link will expire in 24 hours, but a new link can be created calling the API to list past purchases. The CSV file
        will have the same format as the JSON preview: each row will contain a <Link href="#Permit">permit</Link>.
      </Paragraph>

      <Paragraph>
        An error response will have a 4xx status code, and will contain an <code>error</code> string. In particular,
        a lack of payment info or credits will result in a 402 response.
      </Paragraph>

      <Paragraph>
        Multiple calls to the purchase API will result in multiple charges. In case of a network error, use the list API to check
        if the purchase has succeeded.
      </Paragraph>

      <Title level={5}>Sample request</Title>

      <Paragraph>
        Purchase the results from the earlier search:
      </Paragraph>
      
      <Paragraph>
        <SyntaxHighlighter language="bash" style={ prism }>
          { PURCHASE_CODE }
        </SyntaxHighlighter>
      </Paragraph>
      <Title level={5}>Sample response</Title>
      <Paragraph>
        <SyntaxHighlighter language="json" style={ prism } className={ styles.response }>
          { JSON.stringify(PURCHASE_RESPONSE, null, 2) }
        </SyntaxHighlighter>
      </Paragraph>
      <Title level={4}>List past purchases</Title>
      <Paragraph>(Not implemented yet)</Paragraph>

      <Title level={3} id="Permit">Permit properties</Title>

      <Paragraph>Most values can be <code>null</code> if the data is not available</Paragraph>

      <Paragraph>
        <table className={ styles.objectFormat }>
          <thead>
            <tr>
              <th>Name</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><code>jurisdiction</code> <em>string</em></td>
              <td>Name of the jurisdiction where the permit was filed<br />
                E.g.: <code>Los Angeles County</code></td>
            </tr>
            <tr>
              <td><code>record_number</code> <em>string</em></td>
              <td>Permit number<br />
                E.g.: <code>B00-01011</code></td>
            </tr>
            <tr>
              <td><code>category</code> <em>string</em></td>
              <td>
                A comma-separated list of 0 or more categories.<br/>
                <Collapse ghost>
                  <Panel header="Possible values">
                    <ul>
                      { CATEGORIES.map((value, idx) => <li key={idx}><code>{value}</code></li>) }
                    </ul>
                  </Panel>
                </Collapse>
              </td>
            </tr>
            <tr>
              <td><code>apn</code> <em>string</em></td>
              <td>Assessor Parcel Number, in a county-specific format.</td>
            </tr>
            <tr>
              <td><code>street</code> <em>string</em></td>
              <td>Street address: house number, street name, unit number.</td>
            </tr>
            <tr>
              <td><code>city</code> <em>string</em></td>
              <td>Name of the city</td>
            </tr>
            <tr>
              <td><code>state</code> <em>string</em></td>
              <td>2-letter state code</td>
            </tr>
            <tr>
              <td><code>zipcode</code> <em>string</em></td>
              <td>5-digit zipcode</td>
            </tr>
            <tr>
              <td><code>status</code> <em>string</em></td>
              <td>Permit status<br/>
                <Collapse ghost>
                  <Panel header="Possible values">
                    <ul>
                      { STATUSES.map((value, idx) => <li key={idx}><code>{value}</code></li>) }
                    </ul>
                  </Panel>
                </Collapse>
              </td>
            </tr>
            <tr>
              <td><code>file_date</code> <em>string [YYYY-MM-DD]</em></td>
              <td>Date the permit was filed; may be <code>null</code>.<br />E.g.: <code>2010-06-15</code></td>
            </tr>
            <tr>
              <td><code>permit_date</code> <em>string [YYYY-MM-DD]</em></td>
              <td>Date the permit was issued; may be <code>null</code> if not known.<br />E.g.: <code>2010-08-01</code></td>
            </tr>
            <tr>
              <td><code>final_date</code> <em>string [YYYY-MM-DD]</em></td>
              <td>Date the permit was finaled; may be <code>null</code> if the permit has not been finaled yet or has expired.<br />E.g.: <code>2010-08-20</code></td>
            </tr>
            <tr>
              <td><code>description</code> <em>string</em></td>
              <td>Description of the permit as entered by the applicant; may be <code>null</code> in rare cases.<br />
                E.g.: <code>INT REM - KIT/BATH/LAUNDRY/LIGHTING, DOORWAYS ETC.</code></td>
            </tr>
            <tr>
              <td><code>job_value</code> <em>float</em></td>
              <td>Valuation of the permit; may be <code>null</code> if not known.<br />
                E.g.: <code>34546.72</code></td>
            </tr>
            <tr>
              <td><code>total_fees</code> <em>float</em></td>
              <td>Permit fees; may be <code>null</code> if not known.<br />
                E.g.: <code>271.93</code></td>
            </tr>
            <tr>
              <td><code>sq_ft</code> <em>float</em></td>
              <td>Area of the property in ft<sup>2</sup>; may be <code>0</code> or <code>null</code>.<br />
                E.g.: <code>3111</code></td>
            </tr>
            <tr>
              <td><code>contractor_name</code> <em>string</em></td>
              <td>Contractor's name</td>
            </tr>
            <tr>
              <td><code>contractor_biz_name</code> <em>string</em></td>
              <td>Contractor's business name</td>
            </tr>
            <tr>
              <td><code>contractor_street</code> <em>string</em></td>
              <td>Contractor's street address, including any unit numbers</td>
            </tr>
            <tr>
              <td><code>contractor_city</code> <em>string</em></td>
              <td>Contractor's city</td>
            </tr>
            <tr>
              <td><code>contractor_state</code> <em>string</em></td>
              <td>Contractor's state</td>
            </tr>
            <tr>
              <td><code>contractor_zipcode</code> <em>string</em></td>
              <td>Contractor's zipcode</td>
            </tr>
            <tr>
              <td><code>contractor_phone</code> <em>string</em></td>
              <td>Contractor's phone number</td>
            </tr>
            <tr>
              <td><code>contractor_email</code> <em>string</em></td>
              <td>Contractor's email</td>
            </tr>
            <tr>
              <td><code>architect_name</code> <em>string</em></td>
              <td>Architect's name</td>
            </tr>
            <tr>
              <td><code>architect_biz_name</code> <em>string</em></td>
              <td>Architect's business name</td>
            </tr>
            <tr>
              <td><code>architect_street</code> <em>string</em></td>
              <td>Architect's street address, including any unit numbers</td>
            </tr>
            <tr>
              <td><code>architect_city</code> <em>string</em></td>
              <td>Architect's city</td>
            </tr>
            <tr>
              <td><code>architect_state</code> <em>string</em></td>
              <td>Architect's state</td>
            </tr>
            <tr>
              <td><code>architect_zipcode</code> <em>string</em></td>
              <td>Architect's zipcode</td>
            </tr>
            <tr>
              <td><code>architect_phone</code> <em>string</em></td>
              <td>Architect's phone number</td>
            </tr>
            <tr>
              <td><code>architect_email</code> <em>string</em></td>
              <td>Architect's email</td>
            </tr>
            <tr>
              <td><code>designer_name</code> <em>string</em></td>
              <td>Designer's name</td>
            </tr>
            <tr>
              <td><code>designer_biz_name</code> <em>string</em></td>
              <td>Designer's business name</td>
            </tr>
            <tr>
              <td><code>designer_street</code> <em>string</em></td>
              <td>Designer's street address, including any unit numbers</td>
            </tr>
            <tr>
              <td><code>designer_city</code> <em>string</em></td>
              <td>Designer's city</td>
            </tr>
            <tr>
              <td><code>designer_state</code> <em>string</em></td>
              <td>Designer's state</td>
            </tr>
            <tr>
              <td><code>designer_zipcode</code> <em>string</em></td>
              <td>Designer's zipcode</td>
            </tr>
            <tr>
              <td><code>designer_phone</code> <em>string</em></td>
              <td>Designer's phone number</td>
            </tr>
            <tr>
              <td><code>designer_email</code> <em>string</em></td>
              <td>Designer's email</td>
            </tr>
            <tr>
              <td><code>engineer_name</code> <em>string</em></td>
              <td>Engineer's name</td>
            </tr>
            <tr>
              <td><code>engineer_biz_name</code> <em>string</em></td>
              <td>Engineer's business name</td>
            </tr>
            <tr>
              <td><code>engineer_street</code> <em>string</em></td>
              <td>Engineer's street address, including any unit numbers</td>
            </tr>
            <tr>
              <td><code>engineer_city</code> <em>string</em></td>
              <td>Engineer's city</td>
            </tr>
            <tr>
              <td><code>engineer_state</code> <em>string</em></td>
              <td>Engineer's state</td>
            </tr>
            <tr>
              <td><code>engineer_zipcode</code> <em>string</em></td>
              <td>Engineer's zipcode</td>
            </tr>
            <tr>
              <td><code>engineer_phone</code> <em>string</em></td>
              <td>Engineer's phone number</td>
            </tr>
            <tr>
              <td><code>engineer_email</code> <em>string</em></td>
              <td>Engineer's email</td>
            </tr>
          </tbody>
        </table>
      </Paragraph>
    </Layout.Content>
  );
}
